import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { Coins, Sparkles, Trophy } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";

function useCountUp(target) {
  const [val, setVal] = useState(target);
  const prev = useRef(target);
  useEffect(() => {
    const from = prev.current, to = target; prev.current = target;
    if (from === to) return;
    const start = performance.now(), dur = 900;
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / dur);
      setVal(Math.round(from + (to - from) * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target]);
  return val;
}

function JackpotWin({ win, onClose }) {
  const { t } = useT();
  useEffect(() => {
    const colors = ["#fbbf24", "#f59e0b", "#a855f7", "#ffffff"];
    confetti({ particleCount: 220, spread: 130, startVelocity: 50, origin: { y: 0.55 }, colors, zIndex: 10000, scalar: 1.3 });
    const id = setInterval(() => confetti({ particleCount: 30, angle: 90, spread: 100, origin: { x: Math.random(), y: 0.2 }, colors, zIndex: 10000 }), 500);
    setTimeout(() => clearInterval(id), 3000);
    return () => clearInterval(id);
  }, []);
  return createPortal(
    <motion.div data-testid="jackpot-win-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9999] overflow-y-auto bg-black/85 backdrop-blur-md">
      <div className="min-h-full flex items-center justify-center p-4 py-8">
        <motion.div initial={{ scale: 0.6, rotate: -4, opacity: 0 }} animate={{ scale: 1, rotate: 0, opacity: 1 }} transition={{ type: "spring", stiffness: 240, damping: 16 }}
          className="relative w-full max-w-md rounded-3xl border-2 border-amber-400/70 bg-[#120c04] p-8 text-center shadow-[0_0_120px_rgba(251,191,36,0.5)] overflow-hidden">
          <div className="pointer-events-none absolute -top-28 left-1/2 -translate-x-1/2 w-80 h-80 rounded-full bg-gradient-to-br from-amber-400/50 via-orange-500/30 to-purple-600/30 blur-3xl" />
          <motion.div animate={{ scale: [1, 1.12, 1] }} transition={{ repeat: Infinity, duration: 1.4 }}
            className="relative mx-auto mb-4 w-24 h-24 rounded-full bg-gradient-to-br from-amber-300 to-orange-500 flex items-center justify-center shadow-[0_0_50px_rgba(251,191,36,0.8)]">
            <Trophy className="w-12 h-12 text-[#120c04]" strokeWidth={2.5} />
          </motion.div>
          <p className="relative text-xs font-bold tracking-[0.35em] text-amber-300 uppercase">{t("jackpot_hit_label")}</p>
          <h2 className="relative mt-2 font-display font-black text-4xl sm:text-5xl text-amber-200">{t("jackpot_hit_title")}</h2>
          <p data-testid="jackpot-win-amount" className="relative mt-4 font-mono font-black text-5xl text-amber-300 flex items-center justify-center gap-3">
            <Coins className="w-10 h-10" /> +{Number(win.amount).toLocaleString()}
          </p>
          <p className="relative mt-2 text-xs text-amber-200/70">{t("jackpot_no_cash")}</p>
          <button data-testid="jackpot-win-close" onClick={onClose}
            className="relative mt-7 w-full py-3.5 rounded-full font-bold text-[#120c04] bg-gradient-to-r from-amber-300 to-orange-400 hover:brightness-110 active:scale-[0.98] transition-[transform,filter] duration-200">
            {t("jackpot_collect")}
          </button>
        </motion.div>
      </div>
    </motion.div>,
    document.body
  );
}

export default function JackpotTicker({ compact = false }) {
  const [jp, setJp] = useState(null);
  const [win, setWin] = useState(null);
  const { user, refresh } = useAuth();
  const { t } = useT();
  const pool = useCountUp(jp?.pool ?? 0);

  useEffect(() => {
    let on = true;
    const load = async () => {
      try {
        const { data } = await api.get("/jackpot");
        if (!on) return;
        setJp(data);
        if (data.your_win) setWin(data.your_win);
      } catch { /* ignore */ }
    };
    load();
    const id = setInterval(load, compact ? 6000 : 8000);
    return () => { on = false; clearInterval(id); };
  }, [compact, user?.coins]);

  const closeWin = async () => {
    setWin(null);
    try { await api.post("/jackpot/ack"); } catch { /* ignore */ }
    await refresh();
  };

  if (!jp || !jp.enabled) return null;

  const overlay = <AnimatePresence>{win && <JackpotWin win={win} onClose={closeWin} />}</AnimatePresence>;

  if (compact) {
    return (
      <>
        {overlay}
        <div data-testid="jackpot-ticker-compact" className="glass rounded-2xl p-4 border border-amber-500/30 bg-gradient-to-r from-amber-500/10 to-purple-600/10">
          <p className="text-[10px] uppercase tracking-widest text-amber-300/80 font-semibold flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> {t("jackpot_label")}</p>
          <p data-testid="jackpot-pool" className="font-mono font-black text-2xl text-amber-300 mt-1">{pool.toLocaleString()}</p>
          <p className="text-[10px] text-gray-500 mt-1">{t("jackpot_feed_hint")}</p>
        </div>
      </>
    );
  }

  return (
    <>
      {overlay}
      <div data-testid="jackpot-ticker" className="relative rounded-3xl overflow-hidden border border-amber-500/40 mb-8 bg-gradient-to-r from-amber-500/15 via-purple-600/15 to-transparent">
        <div className="pointer-events-none absolute -left-10 -top-16 w-64 h-64 rounded-full bg-amber-400/20 blur-3xl" />
        <div className="relative p-5 lg:p-6 flex flex-col md:flex-row md:items-center gap-5">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <motion.div animate={{ rotate: [0, -6, 6, 0] }} transition={{ repeat: Infinity, duration: 3 }}
              className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-purple-600 flex items-center justify-center shrink-0 shadow-[0_0_24px_rgba(245,158,11,0.45)]">
              <Trophy className="w-7 h-7 text-white" />
            </motion.div>
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-[0.25em] text-amber-300 font-bold flex items-center gap-2">{t("jackpot_label")} <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> LIVE</p>
              <p data-testid="jackpot-pool" className="font-mono font-black text-4xl sm:text-5xl text-amber-300 leading-none mt-1 tabular-nums">{pool.toLocaleString()}</p>
              <p className="text-xs text-gray-400 mt-1.5">{t("jackpot_feed_hint")} · <span className="text-amber-300/80">{t("jackpot_no_cash")}</span></p>
            </div>
          </div>
          <div className="md:w-72 shrink-0">
            <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-1.5">{t("jackpot_recent")}</p>
            {jp.recent_wins?.length ? (
              <ul data-testid="jackpot-recent-wins" className="space-y-1">
                {jp.recent_wins.slice(0, 3).map((w) => (
                  <li key={w.win_id} className="flex items-center justify-between text-xs rounded-lg bg-black/30 px-2.5 py-1.5">
                    <span className="text-gray-300 truncate">{w.name}</span>
                    <span className="font-mono text-amber-300 font-bold">+{Number(w.amount).toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-gray-500 rounded-lg bg-black/30 px-2.5 py-2">{t("jackpot_none_yet")}</p>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
